import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import { formatTimestamp, formatToman, statusLabel } from '@saay/formatting';
import {
  AccordionPanel,
  AlertItem,
  AlertList,
  applyFieldErrors,
  Banner,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  FieldErrorList,
  LoadMore,
  Money,
  MoneyField,
  Skeleton,
  Table,
  TextArea,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import type {
  AdminMerchant,
  ApprovalRef,
  CapacityAdjustment,
  CapacityAdjustmentRequest,
} from '../api.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import { AdminCommandFeedback, useAdminFeedback } from '../components/admin-feedback.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import { approvalSubmitLabel, canUse, executesApprovalDirectly } from '../permissions.js';
import { listStatus, setupKeys, useCapacityAdjustments } from '../queries.js';
import { OnDemandCopy } from '../components/on-demand-copy.js';
import { CAPACITY_CHANGE_FULL_FA, CAPACITY_CHANGE_SHORT_FA } from '../ux-copy.js';
import { reasonSchema } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Merchant capacity on the merchant drawer (AC §18.4.8 M11, M12; F-5; D-61). A manager
 * (`APPROVAL_EXECUTE_DIRECT`) sets the limit at once (`201 CapacityAdjustment`,
 * `approvalRequestId` null). Anyone else still receives `202 ApprovalPending`. Both answers are
 * handled; the button label follows the capability.
 */

/** The pending approvals of the merchant (`AdminMerchant.pendingApprovals`, AC §18.4.2). */
export function PendingApprovals({ approvals }: { readonly approvals: readonly ApprovalRef[] }) {
  if (approvals.length === 0) {
    return null;
  }
  return (
    <SurfaceSheetSection id="merchant-pending-approvals" title="درخواست‌های در انتظار تأیید">
      <AlertList label="درخواست‌های در انتظار تأیید">
        {approvals.map((approval) => (
          <AlertItem
            action={<Link to={`/approvals/${approval.approvalRequestId}`}>مشاهدهٔ درخواست</Link>}
            icon="clock"
            key={approval.approvalRequestId}
            text={
              <>
                {'ثبت '}
                <time dateTime={approval.requestedAt}>{formatTimestamp(approval.requestedAt)}</time>
                {' · مهلت تا '}
                <time dateTime={approval.expiresAt}>{formatTimestamp(approval.expiresAt)}</time>
              </>
            }
            title={statusLabel('approvalActionType', approval.actionType)}
          />
        ))}
      </AlertList>
    </SurfaceSheetSection>
  );
}

function CapacityHistoryTable({ merchant }: { readonly merchant: AdminMerchant }) {
  const history = useCapacityAdjustments(merchant.id);
  const rows = history.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <QueryState
      empty={<EmptyState icon="store" title="هنوز تغییری تأیید نشده است" />}
      error={history.error}
      isEmpty={rows.length === 0}
      onRetry={() => void history.refetch()}
      skeleton={<Skeleton label="در حال بارگذاری تاریخچه…" lines={4} />}
      status={listStatus(history)}
    >
      <Table
        caption="تاریخچه سقف ظرفیت"
        columns={[
          {
            header: 'زمان',
            key: 'time',
            pin: 'start',
            render: (row) => <time dateTime={row.createdAt}>{formatTimestamp(row.createdAt)}</time>,
          },
          {
            header: 'سقف قبلی',
            key: 'previous',
            numeric: true,
            render: (row) => <Money rials={row.previousLimitIrr} />,
          },
          {
            header: 'سقف جدید',
            key: 'next',
            numeric: true,
            render: (row) => <Money rials={row.newLimitIrr} />,
          },
          {
            header: 'درخواست‌کننده',
            key: 'requested',
            render: (row) => row.requestedBy.displayNameFa ?? 'بدون نام',
          },
          {
            header: 'تأییدکننده',
            key: 'approved',
            render: (row) => row.approvedBy?.displayNameFa ?? 'اجرای مستقیم',
          },
          { header: 'دلیل', key: 'reason', render: (row) => row.reason },
          {
            detail: false,
            header: <span className="saay-visually-hidden">درخواست تأیید</span>,
            key: 'approval',
            pin: 'end',
            render: (row) =>
              row.approvalRequestId === null ? (
                '—'
              ) : (
                <Link to={`/approvals/${row.approvalRequestId}`}>درخواست</Link>
              ),
          },
        ]}
        regionLabel="جدول تاریخچه ظرفیت"
        rowKey={(row) => row.id}
        rows={rows}
      />
      {history.isFetchNextPageError ? (
        <ErrorState error={history.error} onRetry={() => void history.fetchNextPage()} />
      ) : null}
      <LoadMore
        hasMore={history.hasNextPage}
        loading={history.isFetchingNextPage}
        onLoadMore={() => void history.fetchNextPage()}
      />
    </QueryState>
  );
}

const requestSchema = z.object({
  newApprovedLimitIrr: z
    .number({ error: 'سقف جدید را به تومان وارد کنید.' })
    .int()
    .min(0, 'سقف نمی‌تواند منفی باشد.')
    .nullable()
    .refine((value) => value !== null, 'سقف جدید را به تومان وارد کنید.'),
  reason: reasonSchema(),
});
type RequestInput = z.input<typeof requestSchema>;

/**
 * The server's used capacity (`capacityUsage.usedCapacityIrr`: unsettled + reserved principal). The
 * limit minus the available capacity is wrong once usage passes the limit (available stops at 0).
 */
function capacityUsedIrr(merchant: AdminMerchant): number | null {
  return merchant.capacity === null ? null : merchant.capacityUsage.usedCapacityIrr;
}

function CapacityRequestDialog({
  historyExpanded,
  merchant,
  onOpenChange,
  self,
}: {
  readonly historyExpanded: boolean;
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly self: AdminSelf;
}) {
  const feedback = useAdminFeedback();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<RequestInput>({
    defaultValues: { newApprovedLimitIrr: null, reason: '' },
    resolver: zodResolver(requestSchema),
  });
  const used = capacityUsedIrr(merchant);
  const current = merchant.capacity?.approvedLimitIrr;
  const newLimit = form.watch('newApprovedLimitIrr');
  const belowUsage =
    used !== null && newLimit !== null && newLimit !== undefined && newLimit < used;

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.merchant(merchant.id) }),
      queryClient.invalidateQueries({ queryKey: setupKeys.merchants }),
      queryClient.invalidateQueries({ queryKey: setupKeys.approvals }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
    ]);
  }

  const command = useCommand<CapacityAdjustmentRequest, CapacityAdjustment>(
    'merchant.capacity.request',
    (client, body, intent) =>
      client.post(`/admin/merchants/${encodeURIComponent(merchant.id)}/capacity-adjustments`, {
        body,
        intent,
      }),
    {
      onApprovalPending: async (approval) => {
        await refresh();
        feedback.approvalSubmitted(approval.approvalRequestId);
        onOpenChange(false);
      },
      onSuccess: async (result) => {
        await refresh();
        feedback.success('سقف ظرفیت ثبت و اجرا شد');
        onOpenChange(false);
        void result;
      },
    },
  );

  async function handle(outcome: CommandOutcome<CapacityAdjustment>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['newApprovedLimitIrr', 'reason'], form.setError));
    if (
      outcome.error.code === 'RESOURCE_VERSION_CONFLICT' ||
      outcome.error.code === 'APPROVAL_REQUEST_PENDING' ||
      outcome.error.code === 'STATE_CONFLICT'
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const pendingId =
    isApiError(error) && error.code === 'APPROVAL_REQUEST_PENDING'
      ? error.details?.approvalRequestId
      : undefined;
  const currentHint = current === undefined ? 'هنوز ثبت نشده' : `فعلی: ${formatToman(current)}`;

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="درخواست تغییر ظرفیت"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          await handle(
            await command.submit({
              expectedVersion: merchant.version,
              newApprovedLimitIrr: values.newApprovedLimitIrr ?? 0,
              reason: values.reason.trim(),
            }),
          );
        })}
      >
        <Controller
          control={form.control}
          name="newApprovedLimitIrr"
          render={({ field }) => (
            <MoneyField
              error={form.formState.errors.newApprovedLimitIrr?.message}
              hint={currentHint}
              label="سقف مصوب جدید"
              name={field.name}
              onChangeRials={field.onChange}
              valueRials={field.value}
            />
          )}
        />
        <p className="admin-muted">
          <OnDemandCopy details={CAPACITY_CHANGE_FULL_FA} short={CAPACITY_CHANGE_SHORT_FA} />
        </p>
        {belowUsage ? (
          <Banner tone="warning">
            {`سقف جدید از مصرف فعلی (${formatToman(used!)}) کمتر است؛ ظرفیت آزاد صفر می‌شود.`}
          </Banner>
        ) : null}
        {canUse(self, 'listCapacityAdjustments') ? (
          <AccordionPanel
            defaultOpenKeys={historyExpanded ? ['history'] : []}
            key={historyExpanded ? 'history-open' : 'history-closed'}
            items={[
              {
                content: <CapacityHistoryTable merchant={merchant} />,
                key: 'history',
                title: 'تاریخچه ظرفیت',
              },
            ]}
            label="تاریخچه ظرفیت"
            type="multiple"
          />
        ) : null}
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        <FieldErrorList errors={unmapped} />
        <AdminCommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {typeof pendingId === 'string' ? (
          <Banner tone="warning">
            یک تغییر ظرفیت دیگر در انتظار تأیید است.{' '}
            <Link to={`/approvals/${pendingId}`}>مشاهدهٔ درخواست</Link>
          </Banner>
        ) : null}
        <Button
          disabled={offline.blocked || command.state.status === 'approval-pending'}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          {approvalSubmitLabel(self)}
        </Button>
      </form>
    </Dialog>
  );
}

/** Capacity change request and inline history (ADM-22). */
export function CapacityActions({
  merchant,
  pendingOpen,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly pendingOpen?: 'history' | 'request' | null;
  readonly self: AdminSelf;
}) {
  const [open, setOpen] = useState<'request' | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  useEffect(() => {
    if (pendingOpen === 'request') {
      setOpen('request');
    }
    if (pendingOpen === 'history') {
      setHistoryExpanded(true);
    }
  }, [pendingOpen]);
  const pendingCapacity = merchant.pendingApprovals.find(
    (approval) => approval.actionType === 'MERCHANT_CAPACITY_CHANGE',
  );
  const canRequest =
    merchant.status !== 'CLOSED' &&
    pendingCapacity === undefined &&
    canUse(self, 'requestCapacityAdjustment');
  const canRead = canUse(self, 'listCapacityAdjustments');
  if (!canRead && !canRequest) {
    return null;
  }
  return (
    <>
      {canRead ? (
        <AccordionPanel
          defaultOpenKeys={historyExpanded ? ['history'] : []}
          key={historyExpanded ? 'history-open' : 'history-closed'}
          items={[
            {
              content: <CapacityHistoryTable merchant={merchant} />,
              key: 'history',
              title: 'تاریخچه ظرفیت',
            },
          ]}
          label="تاریخچه ظرفیت"
          type="multiple"
        />
      ) : null}
      {/* The capacity change opens from «تغییر» beside the approved limit (one entry point). */}
      {open === 'request' ? (
        <CapacityRequestDialog
          historyExpanded={historyExpanded}
          merchant={merchant}
          onOpenChange={(next) => {
            if (!next) setOpen(null);
          }}
          self={self}
        />
      ) : null}
    </>
  );
}
